import React, { useState } from "react";
import {
  BookOpen,
  Search,
  Clock,
  CheckCircle,
  XCircle,
  Plus,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import toast from "react-hot-toast";
import "./BookRequests.css";

const BookRequests = () => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedBook, setSelectedBook] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestReason, setRequestReason] = useState("");

  // Mock data for available books
  const availableBooks = [
    {
      id: 1,
      title: "Advanced Mathematics",
      author: "Dr. Sarah Johnson",
      isbn: "978-1234567890",
      category: "Mathematics",
      available: 3,
      total: 5,
      description: "Comprehensive guide to advanced mathematical concepts",
    },
    {
      id: 2,
      title: "Physics Fundamentals",
      author: "Prof. Michael Chen",
      isbn: "978-1234567891",
      category: "Science",
      available: 2,
      total: 4,
      description: "Essential physics concepts for high school students",
    },
    {
      id: 3,
      title: "World History",
      author: "Dr. Emily Davis",
      isbn: "978-1234567892",
      category: "History",
      available: 1,
      total: 3,
      description: "Complete world history from ancient to modern times",
    },
    {
      id: 4,
      title: "English Literature",
      author: "Prof. James Wilson",
      isbn: "978-1234567893",
      category: "Literature",
      available: 4,
      total: 6,
      description: "Classic and contemporary English literature",
    },
  ];

  // Mock data for student's requests
  const studentRequests = [
    {
      id: 1,
      bookTitle: "Advanced Mathematics",
      requestDate: "2024-01-15",
      status: "pending",
      expectedReturn: "2024-02-15",
    },
    {
      id: 2,
      bookTitle: "Physics Fundamentals",
      requestDate: "2024-01-10",
      status: "approved",
      expectedReturn: "2024-02-10",
    },
    {
      id: 3,
      bookTitle: "World History",
      requestDate: "2024-01-05",
      status: "rejected",
      reason: "Book currently unavailable",
    },
  ];

  const filteredBooks = availableBooks.filter(
    (book) =>
      book.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      book.author.toLowerCase().includes(searchQuery.toLowerCase()) ||
      book.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleRequestBook = (book) => {
    setSelectedBook(book);
    setShowRequestModal(true);
  };

  const submitRequest = () => {
    if (!requestReason.trim()) {
      toast.error("Please provide a reason for the request");
      return;
    }

    // Simulate API call
    toast.success(`Request submitted for "${selectedBook.title}"`);
    setShowRequestModal(false);
    setRequestReason("");
    setSelectedBook(null);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return <Clock size={16} className="text-yellow-500" />;
      case "approved":
        return <CheckCircle size={16} className="text-green-500" />;
      case "rejected":
        return <XCircle size={16} className="text-red-500" />;
      default:
        return <Clock size={16} className="text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "status-pending";
      case "approved":
        return "status-approved";
      case "rejected":
        return "status-rejected";
      default:
        return "status-pending";
    }
  };

  return (
    <div className="book-requests">
      <div className="page-header">
        <div className="header-content">
          <div className="header-icon">
            <BookOpen size={24} />
          </div>
          <div>
            <h1>Book Requests</h1>
            <p>Request books from the library</p>
          </div>
        </div>
      </div>

      <div className="requests-content">
        {/* Search Section */}
        <div className="search-section">
          <div className="search-box">
            <Search size={20} className="search-icon" />
            <input
              type="text"
              placeholder="Search books by title, author, or category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        {/* Available Books */}
        <div className="books-section">
          <h2>Available Books</h2>
          <div className="books-grid">
            {filteredBooks.map((book) => (
              <div key={book.id} className="book-card">
                <div className="book-header">
                  <h3>{book.title}</h3>
                  <span className="book-category">{book.category}</span>
                </div>
                <p className="book-author">by {book.author}</p>
                <p className="book-description">{book.description}</p>
                <div className="book-availability">
                  <span className="available-count">
                    {book.available} of {book.total} available
                  </span>
                </div>
                <button
                  className="request-btn"
                  onClick={() => handleRequestBook(book)}
                  disabled={book.available === 0}
                >
                  <Plus size={16} />
                  {book.available === 0 ? "Unavailable" : "Request Book"}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Student's Requests */}
        <div className="my-requests-section">
          <h2>My Requests</h2>
          <div className="requests-table">
            <div className="table-header">
              <div>Book Title</div>
              <div>Request Date</div>
              <div>Status</div>
              <div>Expected Return</div>
            </div>
            {studentRequests.map((request) => (
              <div key={request.id} className="table-row">
                <div className="book-title">{request.bookTitle}</div>
                <div className="request-date">{request.requestDate}</div>
                <div
                  className={`request-status ${getStatusColor(request.status)}`}
                >
                  {getStatusIcon(request.status)}
                  <span>
                    {request.status.charAt(0).toUpperCase() +
                      request.status.slice(1)}
                  </span>
                </div>
                <div className="expected-return">
                  {request.expectedReturn || "N/A"}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Request Modal */}
      {showRequestModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Request Book</h3>
              <button
                className="modal-close"
                onClick={() => setShowRequestModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="book-info">
                <h4>{selectedBook?.title}</h4>
                <p>by {selectedBook?.author}</p>
              </div>
              <div className="form-group">
                <label htmlFor="reason">Reason for Request</label>
                <textarea
                  id="reason"
                  value={requestReason}
                  onChange={(e) => setRequestReason(e.target.value)}
                  placeholder="Please explain why you need this book..."
                  rows={4}
                  className="reason-textarea"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setShowRequestModal(false)}
              >
                Cancel
              </button>
              <button className="btn-primary" onClick={submitRequest}>
                Submit Request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookRequests;
